<template>
  <div class="mb-8">
    <div class="flex items-center gap-3 mb-4">
      <ActionButton
        :label="buttonLabel"
        icon="fas fa-plus"
        variant="primary"
        @click="$emit('add')"
      />
      <span class="text-black font-medium">: {{ sectionTitle }}</span>
    </div>

    <!-- Loading state -->
    <div v-if="isLoading" class="bg-white rounded-lg border border-gray-200 p-8 text-center">
      <div class="flex items-center justify-center">
        <i class="fas fa-spinner fa-spin text-2xl text-black mr-3"></i>
        <span class="text-black">Chargement des remises...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="bg-white rounded-lg border border-red-200 p-8 text-center">
      <div class="text-red-600 mb-4">
        <i class="fas fa-exclamation-triangle text-2xl mb-2"></i>
        <p>{{ error }}</p>
      </div>
      <button
        @click="$emit('retry')"
        class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
      >
        Réessayer
      </button>
    </div>

    <!-- Table for discounts -->
    <div v-else-if="discounts.length > 0">
      <!-- Desktop: Table -->
      <div class="hidden md:block bg-white rounded-lg border border-gray-200 shadow-sm">
        <ReusableTable
          :columns="columns"
          :data="discounts"
          :sortable="true"
          :show-edit="true"
          :show-delete="true"
        >
          <template #actions="{ row }">
            <div class="flex items-center gap-3">
              <label class="switch" :title="(row as unknown as Discount).actif === 'activé' ? 'Désactiver' : 'Appliquer'">
                <input
                  type="checkbox"
                  :checked="(row as unknown as Discount).actif === 'activé'"
                  @change.stop="$emit('toggle-status', row as unknown as Discount)"
                />
                <span class="slider"></span>
              </label>
              <button
                @click.stop="$emit('delete', row as unknown as Discount)"
                class="w-8 h-8 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center justify-center shadow-sm"
                title="Supprimer"
              >
                <i class="fas fa-trash text-sm"></i>
              </button>
            </div>
          </template>
        </ReusableTable>
      </div>

      <!-- Mobile: Card layout -->
      <div class="md:hidden space-y-3">
        <div
          v-for="(discount, index) in (discounts as unknown as Discount[])"
          :key="index"
          class="bg-white rounded-lg border border-gray-200 shadow-sm p-4"
        >
          <div class="flex justify-between items-start mb-3">
            <h4 class="font-semibold text-black">{{ discount.libelle || 'Sans titre' }}</h4>
            <div class="flex items-center gap-2">
              <label class="switch" :title="discount.actif === 'activé' ? 'Désactiver' : 'Appliquer'">
                <input
                  type="checkbox"
                  :checked="discount.actif === 'activé'"
                  @change.stop="$emit('toggle-status', discount)"
                />
                <span class="slider"></span>
              </label>
              <button
                @click.stop="$emit('delete', discount)"
                class="w-8 h-8 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center justify-center shadow-sm"
                title="Supprimer"
              >
                <i class="fas fa-trash text-sm"></i>
              </button>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span class="text-black">Montant min</span>
              <p class="font-medium text-black">{{ discount.montantMin ?? 0 }}</p>
            </div>
            <div>
              <span class="text-black">Montant max</span>
              <p class="font-medium text-black">{{ discount.montantMax ?? 0 }}</p>
            </div>
            <div>
              <span class="text-black">Date début</span>
              <p class="font-medium text-black">{{ discount.dateDebut || '-' }}</p>
            </div>
            <div>
              <span class="text-black">Date fin</span>
              <p class="font-medium text-black">{{ discount.dateFin || '-' }}</p>
            </div>
            <div>
              <span class="text-black">Taux</span>
              <p class="font-medium text-black">{{ discount.taux != null ? discount.taux + '%' : '0%' }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Empty state -->
    <div v-else class="bg-white rounded-lg border border-gray-200 p-8 text-center">
      <i class="fas fa-percent text-4xl text-black mb-4"></i>
      <p class="text-black">{{ emptyMessage }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ActionButton, ReusableTable } from '@/components'
import type { Discount } from '@/types/discount'

interface Props {
  discounts: Discount[]
  isLoading: boolean
  error: string | null
  sectionTitle: string
  buttonLabel: string
  emptyMessage: string
  columns: Array<{ key: string; title: string; sortable: boolean; align?: string }>
}

interface Emits {
  (e: 'add'): void
  (e: 'retry'): void
  (e: 'delete', row: Discount): void
  (e: 'toggle-status', row: Discount): void
}

defineProps<Props>()
defineEmits<Emits>()
</script>

<style scoped>
/* Switch Bootstrap-like matching PromoCodeTable style */
.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 22px;
  cursor: pointer;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e5e7eb; /* gray-200 */
  transition: 0.3s;
  border-radius: 22px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

input:checked + .slider {
  background-color: #10b981; /* green-500 */
}

input:checked + .slider:before {
  transform: translateX(22px);
}

/* Hover effect */
.switch:hover .slider {
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
}

input:checked + .slider:hover {
  background-color: #059669; /* green-600 */
}
</style>
